import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api.js";
import { CameraScanner } from "../components/CameraScanner.js";
import { Icon } from "../components/Icon.js";
import { useScanner } from "../hooks/useScanner.js";
import { hoursAndMinutes } from "../lib/duration.js";
import { berlinClock } from "../lib/berlin.js";
import type {
  ActiveWork,
  JobListEntry,
  Paused,
  ScanResult,
  Session,
  StandingTask,
  Employee,
} from "../types.js";

type Status =
  | { kind: "idle" }
  | { kind: "busy" }
  // `atTag`: the answer to a tag lookup, said beside the tag box when
  // that box is on screen. At the top it was ~900 px above the box on
  // the tablet, and somebody scrolled down to type a tag saw nothing.
  | { kind: "error"; message: string; atTag?: boolean }
  // Not everything worth saying is a failure. A notice reads as
  // information; an error in red at arm's length reads as "you have done
  // something wrong", which is the wrong thing to tell someone whose
  // timer was fine all along.
  | { kind: "notice"; message: string };

const SCAN_ERRORS: Record<string, string> = {
  not_a_job_label: "That is not a task label. Scan the tag on the cart.",
  job_not_found: "No task has that label.",
  job_closed: "That task is already finished.",
  // Reached by pressing Stop twice when the first one worked and only
  // its reply was lost — a deploy's 502 window is how that happens.
  no_open_session: "That task is already stopped.",
};

/**
 * What Finish asks on the floor. It closes every running timer on the
 * cart, and until 2026-10-29 it only said "for everyone": the floor
 * reported 21 timers in ten days ended this way as unexplained logouts.
 * The leads' Tasks screen already gave the number; this is the same
 * count, which includes the person asking.
 */
export function finishQuestion(name: string, job: JobListEntry | undefined): string {
  if (!job) return `Finish ${name}? It closes the task for everyone, and cannot be undone.`;
  const others = Math.max(0, job.activeEmployees - 1);
  if (others === 0) return `Finish ${name}? Nobody else is working on it. It cannot be undone.`;
  const whose = others === 1 ? "1 other person's timer" : `${others} other people's timers`;
  return `Finish ${name}? It also stops ${whose} on it, and cannot be undone.`;
}

export function WorkScreen({
  employee,
  open,
  active,
  paused,
  onChanged,
  onOpenTasks,
  onOpenDashboard,
  onSignOut,
}: {
  employee: Employee;
  open: Session | null;
  active: ActiveWork | null;
  paused: Paused;
  /** Re-reads what is running; the banner lives above this screen. */
  onChanged: () => void | Promise<void>;
  onOpenTasks: () => void;
  onOpenDashboard: () => void;
  onSignOut: () => void;
}) {
  const [standing, setStanding] = useState<StandingTask[]>([]);
  const [jobs, setJobs] = useState<JobListEntry[]>([]);
  const [scanned, setScanned] = useState<ScanResult | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [manual, setManual] = useState("");
  const [camera, setCamera] = useState(false);
  const [cameraFailed, setCameraFailed] = useState(false);
  const [finishing, setFinishing] = useState<string | null>(null);
  // While a timer runs the lists are hidden, so the screen is Pause and
  // Stop and nothing else to hit by mistake with a glove on. That is
  // right for the floor and wrong for a lead or an admin, who still has
  // an app to use — and whose only way back to the lists was signing
  // out, which "worked" because signing in never asked what was running
  // and the grid came back looking empty. Both halves are fixed; this is
  // the half that does not need the lie.
  const [browsing, setBrowsing] = useState(false);

  const loadLists = useCallback(async () => {
    try {
      const [s, j] = await Promise.all([api.standing(), api.jobs()]);
      setStanding(s.standing);
      setJobs(j.jobs);
    } catch {
      // A failed list must not blank a screen someone is working from.
    }
  }, []);

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  const start = useCallback(
    async (jobId: string, source: "APP" | "SCAN") => {
      setStatus({ kind: "busy" });
      // Read before the call, because onChanged replaces it.
      const previous = active && active.jobId !== jobId ? active.name : null;
      try {
        const { session, alreadyOpen, autoClosedSessionId, autoClosedStale } =
          await api.start(jobId, source);
        setScanned(null);
        setManual("");
        setBrowsing(false);
        // Say it out loud rather than leaving it to be inferred from a
        // banner that did not change. Pressing Start on the task you are
        // already on is what someone does when they think their morning
        // was thrown away — and the answer is that it was not, with the
        // time to prove it.
        setStatus(
          alreadyOpen
            ? {
                kind: "notice",
                message: `Already running since ${berlinClock(session.startedAt)}. Nothing was lost.`,
              }
            : // Switching has always closed the running task; what is new
              // is being able to do it with a tap while one runs, so it
              // now says so. A timer that stops without being mentioned
              // is the thing this screen exists to prevent.
              //
              // The stale case is a different sentence on purpose. That
              // timer's hours are *not* kept, and letting someone assume
              // they were is worse than saying nothing.
              autoClosedSessionId && previous
              ? {
                  kind: "notice",
                  message: autoClosedStale
                    ? `${previous} had been running for more than a day's work, so it was discarded rather than counted. It looked like a timer left on by mistake.`
                    : `Switched from ${previous}. That timer stopped and its time is recorded.`,
                }
              : { kind: "idle" },
        );
        await onChanged();
        void loadLists();
      } catch (err) {
        const code = err instanceof ApiError ? err.code : "";
        setStatus({ kind: "error", message: SCAN_ERRORS[code] ?? "Could not start." });
      }
    },
    [onChanged, loadLists, active],
  );

  const lookUp = useCallback(
    async (code: string) => {
      setStatus({ kind: "busy" });
      try {
        const result = await api.scan(code);
        setScanned(result);
        setStatus({ kind: "idle" });
        setManual("");
        // A scan is a decision already made: start it rather than making
        // someone confirm what they just pointed a scanner at.
        if (result.job.status !== "DONE" && !result.openSessionIsThisJob) {
          await start(result.job.id, "SCAN");
        } else if (result.openSessionIsThisJob && result.openSession) {
          // Said out loud, the same sentence as tapping Start on it.
          // Silence read as a scanner that had not fired, so the tag got
          // scanned again and again.
          setStatus({
            kind: "notice",
            message: `Already running since ${berlinClock(result.openSession.startedAt)}. Nothing was lost.`,
          });
        }
      } catch (err) {
        const code2 = err instanceof ApiError ? err.code : "";
        setScanned(null);
        setStatus({
          kind: "error",
          message: SCAN_ERRORS[code2] ?? "Lookup failed. Check the connection.",
          atTag: true,
        });
      }
    },
    [start],
  );

  // Off while the camera reads, so two readers cannot fire on one tag —
  // but not while a camera that failed to start is merely on screen.
  useScanner(lookUp, status.kind !== "busy" && (!camera || cameraFailed));

  const halt = async (reason: "PAUSED" | "STOPPED") => {
    setStatus({ kind: "busy" });
    const wasOn = active?.name ?? "That task";
    try {
      const { stale } = await api.stop(reason);
      // A timer past the limit is discarded rather than recorded, and
      // somebody who just pressed Stop is entitled to be told. Saying
      // nothing lets them believe a forgotten overnight timer counted as
      // a day's work.
      setStatus(
        stale
          ? {
              kind: "notice",
              message: `${wasOn} had been running for more than a day's work, so it was discarded rather than counted. It looked like a timer left on by mistake.`,
            }
          : { kind: "idle" },
      );
      await onChanged();
      void loadLists();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      // A stop whose request landed and whose reply was lost leaves the
      // timer already stopped, so pressing Stop again answers 409
      // no_open_session. Reporting that as "Could not stop." is the
      // worst answer available: a red error saying the opposite of what
      // happened, to somebody who is now going to find a lead. The app
      // container is recreated on every deploy and Caddy answers 502
      // meanwhile, so this is not a hypothetical window.
      const already = code === "no_open_session" ? SCAN_ERRORS[code] : undefined;
      if (already) {
        setStatus({ kind: "notice", message: already });
        // The screen is still offering Pause and Stop for a timer that
        // is not running. Re-read rather than leaving it saying that.
        await onChanged();
        return;
      }
      // Any other failure: say so and change nothing. A timer the
      // employee can see must not be cleared by a failed request.
      setStatus({ kind: "error", message: "Could not stop." });
    }
  };

  const finish = async (jobId: string) => {
    setStatus({ kind: "busy" });
    try {
      const { closedSessions } = await api.completeJob(jobId);
      setFinishing(null);
      setScanned(null);
      setStatus(
        closedSessions > 1
          ? {
              kind: "notice",
              message: `Task finished. ${closedSessions - 1} other timer(s) stopped.`,
            }
          : { kind: "idle" },
      );
      await onChanged();
      void loadLists();
    } catch (err) {
      // The Stop case again (see halt): a finish whose reply was lost, or
      // one a lead made first from another tablet, answers 409
      // already_closed. The task is finished — say that, and re-read so
      // the banner stops showing a timer the server has closed.
      if (err instanceof ApiError && err.code === "already_closed") {
        setFinishing(null);
        setStatus({ kind: "notice", message: SCAN_ERRORS.job_closed! });
        await onChanged();
        void loadLists();
        return;
      }
      setStatus({ kind: "error", message: "Could not finish the task." });
    }
  };

  const duration = hoursAndMinutes;

  const busy = status.kind === "busy";
  const running = Boolean(open && active);
  const tagShown = !running || browsing;
  const tagError = status.kind === "error" && status.atTag && tagShown ? status.message : null;

  // Beside the box is not enough when a scanner fired while the screen
  // sat scrolled to the top; bring it into view either way.
  const tagErrorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (tagError) tagErrorRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [tagError, status]);

  return (
    <>
      <main>
        <header className="screen-head">
          <div>
            <h1>{employee.displayName}</h1>
            {!running ? (
              <p className="status-line">
                <span className={paused ? "status-dot paused" : "status-dot"} aria-hidden="true" />
                {paused ? "Paused" : "Nothing running"}
              </p>
            ) : null}
          </div>
          <div className="row-actions">
            {/* Admin only, and on the overview rather than buried in the
                task screen — it is not a lead tool. The server enforces
                the same restriction; this only stops offering a button
                that would answer 403. */}
            {employee.role === "ADMIN" ? (
              <button type="button" className="ghost" onClick={onOpenDashboard}>
                Admin
              </button>
            ) : null}
            {employee.role !== "EMPLOYEE" ? (
              <button type="button" className="ghost" onClick={onOpenTasks}>
                Manage Tasks
              </button>
            ) : null}
            <button type="button" className="ghost" onClick={onSignOut}>
              Sign out
            </button>
          </div>
        </header>

        {status.kind === "error" && !tagError ? (
          <p className="error" role="alert">{status.message}</p>
        ) : null}
        {status.kind === "notice" ? (
          <p className="notice" role="status">{status.message}</p>
        ) : null}

        {/* What is running, and the only two things to do about it. The
            name, tag and clock are the banner above this screen; nothing
            here repeats them. */}
        {running && active ? (
          <>
            <div className="split">
              <div className="control">
                <button
                  type="button"
                  className="big"
                  onClick={() => halt("PAUSED")}
                  disabled={busy}
                >
                  <Icon name="pause" size={44} />
                  Pause
                </button>
                <p className="hint">Break — resume in one tap</p>
              </div>
              <div className="control">
                <button
                  type="button"
                  className="big danger"
                  onClick={() => halt("STOPPED")}
                  disabled={busy}
                >
                  <Icon name="stop" size={40} />
                  Stop
                </button>
                <p className="hint">Leave this task for now</p>
              </div>
            </div>
            {active.kind === "JOB" ? (
              finishing === active.jobId ? (
                <div className="confirm">
                  <p>{finishQuestion(active.name, jobs.find((j) => j.id === active.jobId))}</p>
                  <div className="split">
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => setFinishing(null)}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => finish(active.jobId)}
                      disabled={busy}
                    >
                      Yes, finish it
                    </button>
                  </div>
                </div>
              ) : (
                <div className="control">
                  <button
                    type="button"
                    className="ghost wide finish"
                    onClick={() => {
                      setFinishing(active.jobId);
                      // The count the question gives is only worth asking
                      // with if it is current: somebody may have started
                      // on this cart since the list was read.
                      void loadLists();
                    }}
                    disabled={busy}
                  >
                    <Icon name="check" size={28} />
                    Finish task
                  </button>
                  <p className="hint">
                    Only when the whole cart is done — it closes the task for
                    everyone.
                  </p>
                </div>
              )
            ) : null}
            {/* Below Pause, Stop and Finish on purpose: a thumb reaching
                for Stop must never find this instead. */}
            <div className="browse">
              <button
                type="button"
                className="ghost wide"
                onClick={() => setBrowsing((b) => !b)}
                disabled={busy}
              >
                {browsing ? "Hide other tasks" : "Show other tasks"}
                <Icon name={browsing ? "up" : "down"} />
              </button>
              <p className="hint">
                Starting another task stops this one. Its time is kept either
                way.
              </p>
            </div>
          </>
        ) : null}

        {/* Someone came back from a break. One tap to carry on. */}
        {!running && paused ? (
          <section className="card paused-card">
            <div className="row-actions">
              <span className="pill">Paused</span>
              {paused.label ? <code>{paused.label}</code> : null}
            </div>
            <h2>{paused.name ?? "Your task"}</h2>
            {paused.seconds > 0 ? (
              <p className="muted">
                {duration(paused.seconds)} so far. Resuming carries on from
                there.
              </p>
            ) : null}
            <button
              type="button"
              className="primary wide resume"
              onClick={() => start(paused.jobId, "APP")}
              disabled={busy}
            >
              <Icon name="play" size={34} />
              Resume
            </button>
          </section>
        ) : null}

        {!running || browsing ? (
          <>
            <section className="card">
              <h2>{!running && paused ? "Or start something else" : "What are you doing?"}</h2>
              {standing.length === 0 ? (
                <p className="muted">No generic tasks set up.</p>
              ) : (
                <div className="grid">
                  {standing.map((task) => (
                    <button
                      key={task.jobId}
                      type="button"
                      onClick={() => start(task.jobId, "APP")}
                      disabled={busy}
                    >
                      <span className="tile-text">
                        <span className="tile-name">{task.name}</span>
                        {/* Where the work happens lives here, since the shop
                            has one site and says it in the task itself. */}
                        {task.description ? (
                          <span className="tile-note">{task.description}</span>
                        ) : null}
                      </span>
                      <span className="play" aria-hidden="true">
                        <Icon name="play" size={22} />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="card">
              <div>
                <h2>Custom tasks</h2>
                <p className="muted">Scan the tag on the cart, or pick one here.</p>
              </div>

              <form
                className="stack tight"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (manual.trim()) void lookUp(manual.trim());
                }}
              >
                <label htmlFor="tag-code">Tag code</label>
                <div className="scan-row">
                  <input
                    id="tag-code"
                    type="text"
                    value={manual}
                    onChange={(e) => setManual(e.target.value)}
                    placeholder="J7K3M9P"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                  />
                  <button type="submit" className="primary" disabled={busy || !manual.trim()}>
                    Find
                  </button>
                  {!camera ? (
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => {
                        setStatus({ kind: "idle" });
                        setCameraFailed(false);
                        setCamera(true);
                      }}
                    >
                      <Icon name="camera" size={26} />
                      Camera
                    </button>
                  ) : null}
                </div>
                {tagError ? (
                  <p className="error" role="alert" ref={tagErrorRef}>
                    {tagError}
                  </p>
                ) : null}
              </form>

              {jobs.length === 0 ? (
                <p className="muted">Nothing open.</p>
              ) : (
                <ul className="joblist">
                  {jobs.map((job) => (
                    <li key={job.id}>
                      <code>{job.label}</code>
                      <div className="item-text">
                        <span className="item-title">{job.taskType.name}</span>
                        {/* Several carts of different deliveries carry the
                            same task name, so without this the only thing
                            telling two rows apart is a seven-character
                            code. Scanning is exact; picking off the list
                            has to be too. */}
                        <span className="item-meta">
                          {[
                            job.location?.name,
                            job.invoices.length > 0 ? job.invoices.join(" ") : null,
                            job.activeEmployees > 0 ? `${job.activeEmployees} working` : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="primary"
                        onClick={() => start(job.id, "APP")}
                        disabled={busy}
                      >
                        Start
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        ) : null}

        {/* Outside the lists, not inside them. The camera switches the
            hardware scanner off while it is open, so if it lived in a
            section that can disappear — a tile tapped while it is up —
            it would unmount with `camera` still true and leave the
            scanner deaf until the next reload. */}
        {camera ? (
          <CameraScanner
            onScan={(code) => {
              setCamera(false);
              void lookUp(code);
            }}
            onClose={() => setCamera(false)}
            onFailed={() => setCameraFailed(true)}
          />
        ) : null}

        {scanned && scanned.job.status === "DONE" ? (
          <section className="card">
            <p className="muted">
              {scanned.job.taskType.name} ({scanned.job.label}) is already
              finished.
            </p>
          </section>
        ) : null}
      </main>
    </>
  );
}
